import { toBaseFactor } from "@/lib/uom-conversion";

import type { UnitConversion, UnitOfMeasurement } from "@artisancode/api-types";

/** Base-unit qty for a qty typed in `activeUomId`; falls back to the typed qty when units match or no rule exists. */
export function toBaseQty(
  conversions: UnitConversion[],
  qty: number,
  activeUomId: string,
  baseUomId: string,
  isStockReceipt: boolean,
): number {
  if (!isStockReceipt || !activeUomId || activeUomId === baseUomId) return qty;
  const factor = toBaseFactor(conversions, activeUomId, baseUomId);
  return factor === null ? qty : qty * factor;
}

/** "➜ Masuk stok: …" hint shown under a stock-receipt line bought in a non-base unit. */
export function buildConversionHint({
  conversions,
  activeUomId,
  baseUomId,
  baseUomName,
  inputQty,
  pricePerUnit,
}: {
  conversions: UnitConversion[];
  activeUomId: string;
  baseUomId: string;
  baseUomName: string;
  inputQty: number;
  pricePerUnit: number;
}): string | null {
  if (!activeUomId || !baseUomId || activeUomId === baseUomId) return null;
  const factor = toBaseFactor(conversions, activeUomId, baseUomId);
  if (factor === null) return null;

  const converted = inputQty * factor;
  const basePrice = factor > 0 ? pricePerUnit / factor : pricePerUnit;
  return `➜ Masuk stok: ${converted.toLocaleString("id-ID")} ${baseUomName} (@ Rp ${Math.round(basePrice).toLocaleString("id-ID")}/${baseUomName})`;
}

/** A purchase unit is usable only if a conversion rule to the item's base unit exists (the API rejects the rest). */
export function isUomCompatible(
  targetUomId: string,
  baseUomId: string,
  conversions: UnitConversion[],
): boolean {
  if (!baseUomId || targetUomId === baseUomId) return true;
  return toBaseFactor(conversions, targetUomId, baseUomId) !== null;
}

export function filterUomOptions({
  uoms,
  baseUomId,
  activeUomId,
  conversions,
}: {
  uoms: UnitOfMeasurement[];
  baseUomId: string;
  activeUomId: string;
  conversions: UnitConversion[];
}): UnitOfMeasurement[] {
  return uoms.filter(
    (u) =>
      (u.isActive || u.id === activeUomId) &&
      isUomCompatible(u.id, baseUomId, conversions),
  );
}
